/**
 * EmailSignatureSettings — Iter Feb 2026 R47.
 *
 * Two panels stacked:
 *   1. My signature (per user; overrides workspace when set)
 *   2. Workspace default signature (shown to all workspace members)
 *
 * Rendered inside Settings → Email.
 */
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2, Save } from "lucide-react";
import api from "@/lib/api";
import { apiErrorMessage } from "@/lib/apiError";
import { useAuth } from "@/lib/auth";
import SmartRichText from "@/components/SmartRichText";
import RetroSignatureRepair from "@/components/settings/RetroSignatureRepair";

function SignatureCard({ title, description, value, onSave, testid, workspaceId }) {
  const [html, setHtml] = useState(value || "");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setHtml(value || ""); }, [value]);

  const save = async () => {
    setBusy(true);
    try {
      await onSave(html);
      toast.success("Signature saved");
    } catch (e) {
      toast.error(apiErrorMessage(e, "Save failed"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="border bg-white p-3" data-testid={testid}>
      <div className="mb-1">
        <div className="text-sm font-semibold">{title}</div>
        <div className="text-[11px] text-muted-foreground">{description}</div>
      </div>
      {/* R69k · SmartRichText w/ mediaMode=upload gives us image
          upload for free — logos/footer banners get uploaded to
          object storage and embedded as `<img>` in the signature HTML. */}
      <SmartRichText
        value={html}
        onChange={setHtml}
        placeholder="Best regards, …  · Click the image icon in the toolbar to upload a logo or footer banner"
        mediaMode="upload"
        workspaceId={workspaceId}
        minRows={4}
        maxHeight={280}
        testid={`${testid}-editor`}
      />
      {/* R134 · Feb 2026 — Quick image-size utility. Applies a max-width
          to every `<img>` in the current signature so it doesn't render
          huge in the recipient inbox (Gmail respects native resolution
          otherwise). One-click preset, no CSS knowledge needed. */}
      <div className="mt-2 flex items-center gap-2 text-[11px] text-muted-foreground border border-dashed border-border p-1.5">
        <span className="font-semibold">Resize signature images:</span>
        {[120, 180, 240, 320].map((w) => (
          <button key={w} type="button"
                  onClick={() => {
                    const next = html.replace(
                      /<img([^>]*?)\/?>/gi,
                      (m2, attrs) => {
                        const cleaned = attrs
                          .replace(/\swidth="[^"]*"/gi, "")
                          .replace(/\sheight="[^"]*"/gi, "")
                          .replace(/\sstyle="[^"]*"/gi, "");
                        return `<img${cleaned} style="max-width:${w}px;height:auto">`;
                      });
                    setHtml(next);
                  }}
                  className="px-2 py-0.5 border border-border bg-background hover:bg-muted"
                  data-testid={`${testid}-resize-${w}`}>
            {w}px
          </button>
        ))}
        <span className="text-[10px] italic">Applies to every image. Save after resizing.</span>
      </div>
      <div className="flex justify-end mt-2">
        <Button size="sm" onClick={save} disabled={busy} className="rounded-none" data-testid={`${testid}-save`}>
          {busy ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Saving…</> : <><Save className="w-3 h-3 mr-1" /> Save signature</>}
        </Button>
      </div>
    </div>
  );
}

export default function EmailSignatureSettings() {
  const { activeWorkspaceId } = useAuth();
  const [wsSig, setWsSig] = useState("");
  const [meSig, setMeSig] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    if (!activeWorkspaceId) return;
    setLoading(true);
    try {
      const [meRes, wsRes] = await Promise.all([
        api.get("/me/email-signature"),
        api.get(`/workspaces/${activeWorkspaceId}/email-signature`),
      ]);
      setMeSig(meRes.data.signature_html || "");
      setWsSig(wsRes.data.signature_html || "");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [activeWorkspaceId]); // eslint-disable-line

  const saveMe = async (h) => {
    await api.put("/me/email-signature", { signature_html: h });
    setMeSig(h);
  };
  const saveWs = async (h) => {
    await api.put(`/workspaces/${activeWorkspaceId}/email-signature`, { signature_html: h });
    setWsSig(h);
  };

  if (loading) return <div className="text-xs text-muted-foreground py-6 text-center">Loading signatures…</div>;

  return (
    <div className="space-y-4">
      <SignatureCard
        title="My email signature"
        description="Appended to every manual email you send. Overrides the workspace default. Upload a logo/footer image via the toolbar."
        value={meSig}
        onSave={saveMe}
        testid="signature-user"
        workspaceId={activeWorkspaceId}
      />
      <SignatureCard
        title="Workspace default signature"
        description="Used when a user hasn't set a personal signature. Great for a consistent brand sign-off."
        value={wsSig}
        onSave={saveWs}
        testid="signature-workspace"
        workspaceId={activeWorkspaceId}
      />
      {/* R126 · Retro signature repair for historic sent emails. */}
      <RetroSignatureRepair />
    </div>
  );
}
